import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "client" | "agent" | "staff" | "admin" | "super_admin";

export function useUserRoles() {
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasClientRecord, setHasClientRecord] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async (uid: string | null) => {
      if (!uid) {
        if (active) {
          setRoles([]);
          setUserId(null);
          setHasClientRecord(false);
          setLoading(false);
        }
        return;
      }
      const [{ data }, { data: clientRow }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", uid),
        supabase.from("clients").select("id").eq("user_id", uid).maybeSingle(),
      ]);
      if (!active) return;
      setUserId(uid);
      setRoles(((data ?? []).map((r) => r.role)) as AppRole[]);
      setHasClientRecord(Boolean(clientRow));
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data }) => {
      load(data.session?.user?.id ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      load(session?.user?.id ?? null);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const isAgent = roles.includes("agent");
  const isStaff = roles.includes("staff");
  // Client portal access comes from holding a client record, not only a role row.
  const isClient = roles.includes("client") || hasClientRecord;
  const portalCount = [isAdmin, isAgent, isStaff, isClient].filter(Boolean).length;

  return { roles, userId, loading, isAdmin, isAgent, isStaff, isClient, hasClientRecord, portalCount };
}